import { useState } from "react";
import { Link } from "react-router-dom";
import { capture } from "@/lib/posthog";
import { useTheme } from "@/lib/theme";
import "@/styles/home.css";

const GPT_INSTALL_URL = "/login";

const operatorFeatures = [
  {
    title: "1-Click Secure OAuth",
    desc: "No scripts. No manual API setup. No maintenance.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
  {
    title: "Multi-Account Access",
    desc: "Full MCC support for agency-wide deployment.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: "API Integration",
    desc: "Built on the official Google Ads API infrastructure.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
  },
  {
    title: "15 Years Experience",
    desc: "Trained by the logic of 3,000+ managed accounts.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
  {
    title: "Browser Based",
    desc: "100% cloud. No local installs required.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
  },
  {
    title: "Immediate Action",
    desc: "Get clarity before client calls in seconds, not hours.",
    icon: (
      <svg
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
];

const painPoints = [
  "Jumping between dashboards",
  "Manually checking performance shifts",
  "Preparing insights for client calls",
  "Repeating analysis every week",
  "Missing signals because you're moving fast",
  "Preparing for client reporting",
];

const audiences = [
  {
    title: "PPC Agencies",
    desc: "Managing multiple client accounts at scale.",
  },
  {
    title: "Freelance Media Buyers",
    desc: "Who want leverage and faster insights.",
  },
  {
    title: "Consultants",
    desc: "Who need deep clarity before client strategy calls.",
  },
];

function SignupForm({
  id,
  compact = false,
}: {
  id: string;
  compact?: boolean;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    capture("signup_form_submitted", { method: "email" });
    try {
      const res = await fetch("/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus("success");
        setMessage(data.message || "Your account is ready!");
      } else {
        setStatus("error");
        setMessage(data.error || "Something went wrong.");
      }
    } catch {
      setStatus("error");
      setMessage("Network error. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div className="home-waitlist-success">
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <p>{message}</p>
      </div>
    );
  }

  return (
    <div
      className={`home-waitlist ${compact ? "home-waitlist-compact" : ""}`}
      id={id}
    >
      <a
        href="/auth/login"
        className="home-waitlist-google-btn"
        onClick={() => capture("signup_form_submitted", { method: "google" })}
      >
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Sign up with Google
      </a>
      <div className="home-waitlist-divider">
        <span>or</span>
      </div>
      <form onSubmit={handleSubmit} className="home-waitlist-form">
        <input
          type="text"
          placeholder="Your name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder="Email address"
          className="input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={status === "loading"}
        >
          {status === "loading" ? "Creating account..." : "Create Account"}
        </button>
      </form>
      {status === "error" && <p className="home-waitlist-error">{message}</p>}
      <p className="home-waitlist-login">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </div>
  );
}

export default function HomePage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="home-page">
      <nav className="home-nav">
        <div className="home-nav-inner">
          <Link to="/" className="home-logo">
            <img
              src="/Final-white.png"
              alt="Zima Media"
              className="home-logo-img"
            />
          </Link>
          <div className="home-nav-links">
            <Link to="/pricing">Pricing</Link>
            <button
              onClick={toggleTheme}
              className="theme-toggle"
              aria-label={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
              title={
                theme === "dark"
                  ? "Switch to light mode"
                  : "Switch to dark mode"
              }
            >
              {theme === "dark" ? (
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
            <Link to="/login" className="btn btn-primary">
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      <section className="home-hero">
        <div className="hero-glow" />
        <div className="home-hero-content">
          <p className="home-hero-eyebrow">
            Built by Zima Media — the first ChatGPT plugin with direct, live access to Google Ads.
          </p>
          <h1>
            Control <span className="gradient-text">Google Ads</span>
            <br />
            from <span className="gradient-text">ChatGPT</span>
          </h1>
          <p className="home-hero-subtitle">
            No installs. No scripts. No desktop apps. Direct AI access to your
            accounts via the official Google Ads API.
          </p>
          <SignupForm id="hero-signup" />
        </div>
      </section>

      <section className="home-stats">
        <div className="home-section-inner">
          <div className="home-stats-grid">
            <div className="home-stat">
              <span className="home-stat-number">3,000+</span>
              <span className="home-stat-label">Accounts Managed</span>
            </div>
            <div className="home-stat">
              <span className="home-stat-number">$20M+</span>
              <span className="home-stat-label">Ad Spend Under Management</span>
            </div>
            <div className="home-stat">
              <span className="home-stat-number">15yrs</span>
              <span className="home-stat-label">Agency Experience</span>
            </div>
          </div>
          <blockquote className="home-quote">
            "The internal AI layer we built for our own agency team — now
            available to other operators."
          </blockquote>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            You Already Know <span className="gradient-text">Google Ads</span>.
            <br />
            You Just Don't Have Time.
          </h2>
          <p className="home-section-subtitle">
            Managing multiple accounts shouldn't mean jumping between endless
            dashboards and manual reports.
          </p>
          <div className="home-pain-list">
            {painPoints.map((point) => (
              <div key={point} className="home-pain-item">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                <span>{point}</span>
              </div>
            ))}
          </div>
          <p className="home-pain-conclusion">
            You don't need another dashboard.{" "}
            <strong>You need leverage.</strong>
          </p>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            Your Google Ads <span className="gradient-text">Copilot</span>{" "}
            Inside ChatGPT
          </h2>
          <p className="home-section-subtitle">
            Connect your account once. Then ask anything. Performance analysis,
            optimization feedback, account summaries — all in plain English.
          </p>
          <div className="home-copilot-features">
            <div className="home-copilot-item">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              <span>Performance analysis</span>
            </div>
            <div className="home-copilot-item">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
              <span>Optimization feedback</span>
            </div>
            <div className="home-copilot-item">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              <span>Account summaries</span>
            </div>
            <div className="home-copilot-item">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              <span>Direct Google Ads API integration</span>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-plugin-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            Get the <span className="gradient-text">ChatGPT Plugin</span>
          </h2>
          <div className="home-plugin-card">
            <div className="home-plugin-logo">
              <img src="/favicon/apple-touch-icon.png" alt="Zima Media" />
            </div>
            <div className="home-plugin-info">
              <h3>Zima Media — Google Ads GPT</h3>
              <p>
                Talk to Google Ads from ChatGPT. Performance analysis,
                optimization feedback, and account summaries — all in plain
                English.
              </p>
              <div className="home-plugin-meta">
                <span className="home-plugin-rating">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                    stroke="none"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                    stroke="none"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                    stroke="none"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                    stroke="none"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="currentColor"
                    stroke="none"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </span>
                <span className="home-plugin-category">Productivity</span>
              </div>
            </div>
            <div className="home-plugin-actions">
              <Link
                to={GPT_INSTALL_URL}
                className="home-plugin-btn"
                onClick={() => capture("gpt_install_clicked")}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Install
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            Built for <span className="gradient-text">Serious Operators</span>
          </h2>
          <div className="home-operators-grid">
            {operatorFeatures.map((feature) => (
              <div key={feature.title} className="home-feature-card">
                <div className="home-feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section home-security-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            Your Data Stays <span className="gradient-text">Yours</span>
          </h2>
          <p className="home-section-subtitle">Security First</p>
          <div className="home-security-grid">
            <div className="home-security-col">
              <h3>Compliance Ready</h3>
              <ul>
                <li>
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Official OAuth connection
                </li>
                <li>
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Access revocable anytime
                </li>
                <li>
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Encrypted communication
                </li>
              </ul>
            </div>
            <div className="home-security-col">
              <h3>Operator Control</h3>
              <ul>
                <li>
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Direct API, no scraping
                </li>
                <li>
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  No selling account data
                </li>
                <li>
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Built for agency standards
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            Built <span className="gradient-text">For</span>
          </h2>
          <div className="home-audience-grid">
            {audiences.map((a) => (
              <div key={a.title} className="home-audience-card">
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="home-section-inner">
          <h2 className="home-section-title">
            Roadmap: <span className="gradient-text">Version 0.1</span>
          </h2>
          <p className="home-section-subtitle">15 agencies and counting. Yes, 15 — we don't inflate numbers.</p>
          <div className="home-roadmap">
            <div className="home-roadmap-item">
              <span className="home-roadmap-badge badge-available">
                Available
              </span>
              <h3>Core Performance</h3>
              <p>
                Secure connection, performance analysis, structured
                optimization.
              </p>
            </div>
            <div className="home-roadmap-item">
              <span className="home-roadmap-badge badge-progress">
                In Progress
              </span>
              <h3>Advanced Logic</h3>
              <p>
                Cross-account monitoring, anomaly alerts, and multi-platform
                support.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="home-final-cta">
        <div className="home-section-inner">
          <h2>
            Stop Digging. <span className="gradient-text">Start Asking.</span>
          </h2>
          <SignupForm id="footer-signup" compact />
          <p className="home-final-cta-sub">
            Free to start. Built for agencies and PPC operators.
          </p>
          <p className="home-final-cta-powered">Powered by Zima Media.</p>
        </div>
      </section>

      <footer className="home-footer">
        <div className="home-footer-inner">
          <p>
            &copy; {new Date().getFullYear()}{" "}
            <a href="https://zimamedia.com" target="_blank" rel="noopener noreferrer">Zima Media</a>. All rights reserved.
          </p>
          <div className="home-footer-links">
            <a href="https://zimamedia.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <a href="https://zimamedia.com/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
